import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Header from '../Header';

/**
 * SEO SERVICE PAGE
 * Theme: 60% Aqua Haze (#F0F4F8) | 30% River Bed (#4A5568) | 10% Cerulean Blue (#2B6CB0)
 * Headline: High-Contrast Authority (#020617)
 */
const SERVICES = [
  { title: 'Search Intel', desc: 'Decoding user intent through deep-layered keyword clusters.' },
  { title: 'Performance', desc: 'Technical audits that turn slow sites into high-speed search favorites.' },
  { title: 'Content Design', desc: 'Crafting authoritative narratives that satisfy both humans and algorithms.' },
  { title: 'Nexus Links', desc: 'Strategic outreach to build high-authority digital ecosystems.' },
  { title: 'Pulse Analytics', desc: 'Real-time monitoring and adaptive strategy shifts based on live data.' },
  { title: 'Global SEO', desc: 'Breaking borders with multi-regional and multilingual search dominance.' }
];

const SEO = () => {
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.05]);

  return (
    <div className="min-h-screen bg-[#F0F4F8] text-[#4A5568] font-sans">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 bg-white border-b border-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-8xl font-black mb-6 tracking-tighter text-[#020617] uppercase">
              Search <span className="text-[#2B6CB0]">Engine</span> Optimization
            </h1>
            <p className="text-xl text-[#4A5568] max-w-3xl mx-auto mb-10 leading-relaxed font-medium">
              We don't just find traffic. We engineer authority for brands that refuse to be second, 
              blending technical precision with creative content strategy.
            </p>
            <Link to="/contact" className="inline-block bg-[#2B6CB0] text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-[#020617] transition-all shadow-lg shadow-[#2B6CB0]/20">
              Start Your Audit
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Feature Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
            <div>
              <h2 className="text-4xl md:text-5xl font-black mb-8 tracking-tighter text-[#020617] uppercase">
                Visibility is <br/> Authority
              </h2>
              <p className="text-lg text-[#4A5568] mb-8 leading-relaxed font-medium">
                In a crowded digital landscape, being discovered is the ultimate competitive advantage. 
                Our methodology treats SEO as a data-driven art form—precise, intentional, and undeniably effective.
              </p>
              
              <div className="flex gap-12 border-t border-[#4A5568]/10 pt-8">
                <div>
                  <span className="block text-5xl font-black tracking-tighter text-[#2B6CB0]">98%</span>
                  <span className="text-xs uppercase tracking-[0.2em] font-black text-[#4A5568]/60">Visibility Increase</span>
                </div>
                <div>
                  <span className="block text-5xl font-black tracking-tighter text-[#2B6CB0]">24/7</span>
                  <span className="text-xs uppercase tracking-[0.2em] font-black text-[#4A5568]/60">Live Monitoring</span>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl shadow-2xl border-8 border-white">
              <motion.img
                style={{ scale }}
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"
                alt="SEO Analytics"
                className="w-full h-[450px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>

          {/* Services Grid */}
          <div className="mb-32">
            <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter text-[#020617] uppercase">
              Our SEO <span className="text-[#2B6CB0]">Methodology</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((service, index) => (
                <div key={index} className="bg-white p-10 rounded-2xl border border-[#F0F4F8] shadow-sm hover:border-[#2B6CB0]/30 transition-all group">
                  <div className="w-10 h-1 bg-[#2B6CB0] mb-6 group-hover:w-20 transition-all"></div>
                  <h3 className="text-sm font-black mb-4 text-[#020617] uppercase tracking-widest leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed font-medium">
                    {service.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA Card */}
          <div className="text-center bg-[#020617] p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2B6CB0] opacity-10 rounded-full -mr-32 -mt-32"></div>
            
            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-white uppercase">
              Ready to be <span className="text-[#2B6CB0]">Unmissable?</span>
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto font-medium">
              Let's perform a technical audit and build a strategy that puts your brand at the top of search results.
            </p>
            <div className="flex flex-wrap gap-4 justify-center relative z-10">
              <Link to="/contact" className="bg-[#2B6CB0] text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all">
                Get Started Now
              </Link>
              <Link to="/services" className="border-2 border-white/20 text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all">
                All Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEO;